import React, { useState, useEffect, useRef, useCallback } from 'react';
import { verifyPassword, updatePassword } from '../utils/api';
import HelpButton from '../components/shared/HelpButton';
import ContractViewer from '../components/ContractViewer';

/* ── Lock SVG ── */
const LockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22d3ee" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

/* ── Shield icons ── */
const ShieldCheck = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

const ShieldX = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <line x1="9" y1="9" x2="15" y2="15" />
    <line x1="15" y1="9" x2="9" y2="15" />
  </svg>
);

/* ── Eye toggle ── */
const EyeToggle = ({ show, onClick }) => (
  <button type="button" onClick={onClick} style={s.eyeBtn}>
    {show ? (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </svg>
    ) : (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    )}
  </button>
);

/* ── Strength calculator ── */
const getStrength = (pw) => {
  if (!pw) return { label: '', color: '', width: '0%' };
  let score = 0;
  if (pw.length >= 6) score++;
  if (pw.length >= 10) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 1) return { label: 'Weak', color: '#f87171', width: '20%' };
  if (score === 2) return { label: 'Fair', color: '#fbbf24', width: '40%' };
  if (score === 3) return { label: 'Good', color: '#22d3ee', width: '60%' };
  if (score === 4) return { label: 'Strong', color: '#4ade80', width: '80%' };
  return { label: 'Very Strong', color: '#4ade80', width: '100%' };
};

const ChangePassword = () => {
  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  /* ── Verification state ── */
  const [oldVerified, setOldVerified] = useState(null); // null = not checked, true = verified, false = wrong
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifiedPassword, setVerifiedPassword] = useState(''); // the password that was verified
  const verifyTimerRef = useRef(null);

  /* ── Toast auto-dismiss ── */
  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  /* ── Verify old password (debounced) ── */
  const doVerify = useCallback(async (password) => {
    if (!password || password.length < 1) {
      setOldVerified(null);
      setVerifiedPassword('');
      return;
    }
    setIsVerifying(true);
    try {
      const result = await verifyPassword(password);
      if (result.verified) {
        setOldVerified(true);
        setVerifiedPassword(password);
      } else {
        setOldVerified(false);
        setVerifiedPassword('');
      }
    } catch {
      setOldVerified(false);
      setVerifiedPassword('');
    } finally {
      setIsVerifying(false);
    }
  }, []);

  /* ── Auto-verify on mount if autofilled ── */
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.oldPassword) {
        doVerify(formData.oldPassword);
      }
    }, 1500);
    return () => clearTimeout(timer);
    // Only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Debounced verify when old password changes ── */
  const handleOldPasswordChange = (value) => {
    setFormData((prev) => ({ ...prev, oldPassword: value }));

    // Reset verification if password changed from what was verified
    if (value !== verifiedPassword) {
      setOldVerified(null);
      setVerifiedPassword('');
    }

    // Clear new password fields if old password changes after verification
    if (oldVerified === true && value !== verifiedPassword) {
      setFormData((prev) => ({ ...prev, oldPassword: value, newPassword: '', confirmPassword: '' }));
      setErrors({});
    }

    // Debounce the verify call
    if (verifyTimerRef.current) clearTimeout(verifyTimerRef.current);
    verifyTimerRef.current = setTimeout(() => {
      doVerify(value);
    }, 3000);
  };

  /* ── Verify button (manual trigger) ── */
  const handleVerifyClick = () => {
    if (formData.oldPassword) {
      doVerify(formData.oldPassword);
    }
  };

  /* ── Field handler with cross-validation ── */
  const setValue = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    const e = { ...errors };

    if (field === 'newPassword') {
      if (value && value.length < 6) e.newPassword = 'Password must be at least 6 characters';
      else if (value && /\s/.test(value)) e.newPassword = 'Password cannot contain spaces';
      else if (value && value === formData.oldPassword) e.newPassword = 'New password must be different from current';
      else delete e.newPassword;
      if (formData.confirmPassword && formData.confirmPassword !== value) e.confirmPassword = 'Passwords do not match';
      else delete e.confirmPassword;
    }

    if (field === 'confirmPassword') {
      if (value && value !== formData.newPassword) e.confirmPassword = 'Passwords do not match';
      else delete e.confirmPassword;
    }

    setErrors(e);
  };

  const isFormValid = () =>
    oldVerified === true &&
    formData.oldPassword === verifiedPassword &&
    formData.newPassword.length >= 6 &&
    !/\s/.test(formData.newPassword) &&
    formData.newPassword !== formData.oldPassword &&
    formData.newPassword === formData.confirmPassword &&
    Object.keys(errors).length === 0;

  const handleSubmit = async () => {
    if (!isFormValid() || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const result = await updatePassword(formData.oldPassword, formData.newPassword);
      if (result.success) {
        setToast({ type: 'success', message: result.message || 'Password updated! Redirecting…' });
        setTimeout(() => { window.location.href = '/'; }, 2000);
      } else {
        setToast({ type: 'error', message: result.message || 'Failed to update password' });
        setIsSubmitting(false);
      }
    } catch (err) {
      setToast({ type: 'error', message: err.message || 'Failed to update password' });
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({ oldPassword: '', newPassword: '', confirmPassword: '' });
    setErrors({});
    setOldVerified(null);
    setVerifiedPassword('');
    setShowOld(false);
    setShowNew(false);
    setShowConfirm(false);
  };

  const strength = getStrength(formData.newPassword);

  /* ── All checks summary ── */
  const checks = [
    { label: 'Current password verified', pass: oldVerified === true },
    { label: 'Min 6 characters', pass: formData.newPassword.length >= 6 },
    { label: 'No spaces', pass: formData.newPassword.length > 0 && !/\s/.test(formData.newPassword) },
    { label: 'Different from current', pass: formData.newPassword.length > 0 && formData.newPassword !== formData.oldPassword },
    { label: 'Passwords match', pass: formData.confirmPassword.length > 0 && formData.newPassword === formData.confirmPassword },
  ];

  return (
    <div style={s.wrapper}>
      <style>{s.keyframes}</style>

      {/* ── Toast ── */}
      {toast && (
        <div style={{
          ...s.toast,
          ...(toast.type === 'success' ? s.toastSuccess : s.toastError),
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {toast.type === 'success'
              ? <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              : <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />}
          </svg>
          {toast.message}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px'}}>
      <h2 style={s.title}>Change Password</h2>
      <HelpButton helpData={[
            { chip: 'changepassword', urlFilter: '/static/pdffilters/PasswordChange.pdf' }
          ]} />
          </div>
        
 
        {pdfUrl && (
          <ContractViewer pdfUrl={pdfUrl} onClose={() => setPdfUrl(null)} />
        )}
      <p style={s.subtitle}>Enter your current password and choose a new one</p>

      {/* ── Warning ── */}
      <div style={s.section}>
        <div style={s.infoBanner}>
          <LockIcon />
          <span>You will be logged out after changing your password</span>
        </div>
      </div>

      {/* ── Current Password ── */}
      <div style={s.section}>
        <div style={s.sectionLabel}>Step 1 — Verify Current Password</div>
        <div style={s.fieldGroup}>
          <label style={s.label}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <LockIcon /> Current Password <span style={{ color: '#f87171' }}>*</span>
            </span>
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type={showOld ? 'text' : 'password'}
              value={formData.oldPassword}
              onChange={(e) => handleOldPasswordChange(e.target.value)}
              placeholder="Enter current password"
              style={{
                ...s.input,
                paddingRight: 42,
                ...(oldVerified === true ? s.inputVerified : {}),
                ...(oldVerified === false ? s.inputError : {}),
              }}
              autoComplete="current-password"
            />
            <EyeToggle show={showOld} onClick={() => setShowOld(!showOld)} />
          </div>

          {/* Verify button */}
          {formData.oldPassword && oldVerified !== true && (
            <button
              onClick={handleVerifyClick}
              disabled={isVerifying}
              style={{
                ...s.verifyBtn,
                ...(isVerifying ? { opacity: 0.6, cursor: 'not-allowed' } : {}),
              }}
            >
              {isVerifying ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span style={s.spinnerSmall} /> Verifying…
                </span>
              ) : (
                'Verify Password'
              )}
            </button>
          )}
        </div>

        {/* ── Verification Status Card ── */}
        {oldVerified !== null && (
          <div style={{
            ...s.verifyCard,
            ...(oldVerified ? s.verifyCardSuccess : s.verifyCardError),
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {oldVerified ? <ShieldCheck /> : <ShieldX />}
              <div>
                <div style={{
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: oldVerified ? '#4ade80' : '#f87171',
                }}>
                  {oldVerified ? 'Password Verified' : 'Password Incorrect'}
                </div>
                <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
                  {oldVerified
                    ? 'Your current password has been confirmed'
                    : 'Please check and try again'}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── New Password (only shown after verification) ── */}
      {oldVerified === true && (
        <>
          <div style={s.section}>
            <div style={s.sectionLabel}>Step 2 — Choose New Password</div>
            <div style={s.fieldGroup}>
              <label style={s.label}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <LockIcon /> New Password <span style={{ color: '#f87171' }}>*</span>
                </span>
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showNew ? 'text' : 'password'}
                  value={formData.newPassword}
                  onChange={(e) => setValue('newPassword', e.target.value)}
                  placeholder="Min 6 characters, no spaces"
                  style={{
                    ...s.input,
                    paddingRight: 42,
                    ...(errors.newPassword ? s.inputError : {}),
                  }}
                  autoComplete="new-password"
                />
                <EyeToggle show={showNew} onClick={() => setShowNew(!showNew)} />
              </div>

              {errors.newPassword && (
                <div style={s.errorMsg}>
                  <svg width="14" height="14" fill="#f87171" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {errors.newPassword}
                </div>
              )}

              {/* Strength bar */}
              {formData.newPassword && !errors.newPassword && (
                <div style={{ marginTop: 8 }}>
                  <div style={s.strengthTrack}>
                    <div style={{ ...s.strengthFill, width: strength.width, background: strength.color }} />
                  </div>
                  <div style={{ fontSize: '0.7rem', color: strength.color, marginTop: 4, fontWeight: 500 }}>
                    {strength.label}
                  </div>
                </div>
              )}
            </div>

            {/* Confirm */}
            <div style={{ ...s.fieldGroup, marginTop: 14 }}>
              <label style={s.label}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <LockIcon /> Confirm New Password <span style={{ color: '#f87171' }}>*</span>
                </span>
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showConfirm ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => setValue('confirmPassword', e.target.value)}
                  placeholder="Re-enter new password"
                  style={{
                    ...s.input,
                    paddingRight: 42,
                    ...(errors.confirmPassword ? s.inputError : {}),
                  }}
                  autoComplete="new-password"
                />
                <EyeToggle show={showConfirm} onClick={() => setShowConfirm(!showConfirm)} />
              </div>

              {errors.confirmPassword && (
                <div style={s.errorMsg}>
                  <svg width="14" height="14" fill="#f87171" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {errors.confirmPassword}
                </div>
              )}
            </div>
          </div>

          {/* ── Checklist Summary ── */}
          {formData.newPassword && (
            <div style={s.section}>
              <div style={s.sectionLabel}>Validation Checks</div>
              <div style={s.checklistCard}>
                {checks.map((c, i) => (
                  <div key={i} style={s.checkRow}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                      stroke={c.pass ? '#4ade80' : 'rgba(255,255,255,0.2)'}
                      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    >
                      {c.pass
                        ? <path d="M5 13l4 4L19 7" />
                        : <circle cx="12" cy="12" r="10" />}
                    </svg>
                    <span style={{
                      fontSize: '0.8rem',
                      color: c.pass ? '#4ade80' : 'rgba(255,255,255,0.35)',
                      fontWeight: c.pass ? 500 : 400,
                    }}>
                      {c.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Ready Summary ── */}
          {isFormValid() && (
            <div style={s.section}>
              <div style={s.summaryCard}>
                <div style={s.summaryHeader}>
                  <div style={s.summaryIcon}><ShieldCheck /></div>
                  <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#4ade80' }}>Ready to Update</span>
                </div>
                <div style={s.summaryGrid}>
                  <div style={s.summaryItem}>
                    <div style={s.summaryItemLabel}>Length</div>
                    <div style={s.summaryItemValue}>{formData.newPassword.length} characters</div>
                  </div>
                  <div style={s.summaryItem}>
                    <div style={s.summaryItemLabel}>Strength</div>
                    <div style={{ ...s.summaryItemValue, color: strength.color }}>{strength.label}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Actions ── */}
          <div style={s.section}>
            <div style={s.btnRow}>
              <button onClick={handleReset} style={s.resetBtn}>Reset</button>
              <button
                onClick={handleSubmit}
                disabled={!isFormValid() || isSubmitting}
                style={{
                  ...s.submitBtn,
                  ...((!isFormValid() || isSubmitting) ? s.submitBtnDisabled : {}),
                }}
              >
                {isSubmitting ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <span style={s.spinner} />
                    Updating…
                  </span>
                ) : (
                  'Update Password'
                )}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

/* ── Styles matching formStyles / RecordMeetingForm pattern ── */
const s = {
  keyframes: `@keyframes spin { to { transform: rotate(360deg); } }`,

  wrapper: {
    padding: "24px 20px 40px",
    maxWidth: 720,
    margin: "0 auto",
    fontFamily: "'DM Sans', sans-serif",
    color: "#f1f5f9",
  },
  title: {
    fontSize: "1.15rem",
    fontWeight: 700,
    color: "#f1f5f9",
    marginBottom: 4,
    marginTop: 0,
  },
  subtitle: {
    fontSize: "0.8rem",
    color: "rgba(255,255,255,0.45)",
    marginBottom: 24,
    marginTop: 0,
  },
  section: {
    marginBottom: 28,
  },
  sectionLabel: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#22d3ee",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    marginBottom: 14,
    marginTop: 0,
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 5,
  },
  label: {
    fontSize: "0.78rem",
    fontWeight: 500,
    color: "rgba(255,255,255,0.6)",
  },
  input: {
    padding: "10px 14px",
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,0.15)",
    background: "rgba(255,255,255,0.07)",
    color: "#f1f5f9",
    fontSize: "0.85rem",
    fontFamily: "'DM Sans', sans-serif",
    outline: "none",
    transition: "border-color 0.2s ease, background 0.2s ease",
    width: "100%",
    boxSizing: "border-box",
  },
  inputError: {
    borderColor: "rgba(239,68,68,0.5)",
    background: "rgba(239,68,68,0.05)",
  },
  inputVerified: {
    borderColor: "rgba(34,197,94,0.5)",
    background: "rgba(34,197,94,0.05)",
  },
  errorMsg: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: "0.75rem",
    color: "#f87171",
    marginTop: 4,
  },
  eyeBtn: {
    position: "absolute",
    right: 12,
    top: "50%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 4,
    display: "flex",
    alignItems: "center",
    color: "rgba(255,255,255,0.4)",
    transition: "color 0.2s ease",
  },
  infoBanner: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 14px",
    borderRadius: 10,
    background: "rgba(245,158,11,0.1)",
    border: "1px solid rgba(245,158,11,0.2)",
    color: "#fbbf24",
    fontSize: "0.8rem",
    fontWeight: 500,
  },
  verifyBtn: {
    marginTop: 8,
    padding: "10px 16px",
    borderRadius: 10,
    border: "1px solid rgba(34,211,238,0.3)",
    background: "rgba(34,211,238,0.1)",
    color: "#22d3ee",
    fontSize: "0.82rem",
    fontWeight: 600,
    fontFamily: "'DM Sans', sans-serif",
    cursor: "pointer",
    transition: "all 0.2s ease",
    width: "100%",
    textAlign: "center",
  },
  verifyCard: {
    marginTop: 14,
    padding: "14px 16px",
    borderRadius: 12,
  },
  verifyCardSuccess: {
    background: "rgba(34,197,94,0.08)",
    border: "1px solid rgba(34,197,94,0.25)",
  },
  verifyCardError: {
    background: "rgba(239,68,68,0.08)",
    border: "1px solid rgba(239,68,68,0.25)",
  },
  spinnerSmall: {
    display: "inline-block",
    width: 14,
    height: 14,
    border: "2px solid rgba(34,211,238,0.3)",
    borderTopColor: "#22d3ee",
    borderRadius: "50%",
    animation: "spin 0.6s linear infinite",
  },
  strengthTrack: {
    height: 4,
    borderRadius: 2,
    background: "rgba(255,255,255,0.08)",
    overflow: "hidden",
  },
  strengthFill: {
    height: "100%",
    borderRadius: 2,
    transition: "all 0.3s ease",
  },
  checklistCard: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    padding: "16px",
    borderRadius: 12,
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.08)",
  },
  checkRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  summaryCard: {
    background: "rgba(34,197,94,0.05)",
    border: "1px solid rgba(34,197,94,0.2)",
    borderRadius: 16,
    padding: 20,
  },
  summaryHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 14,
  },
  summaryIcon: {
    width: 32,
    height: 32,
    background: "rgba(34,197,94,0.2)",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
  },
  summaryItem: {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(34,197,94,0.15)",
    borderRadius: 10,
    padding: "10px 12px",
  },
  summaryItemLabel: {
    fontSize: "0.7rem",
    color: "rgba(34,197,94,0.6)",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: 2,
  },
  summaryItemValue: {
    fontSize: "0.88rem",
    fontWeight: 600,
    color: "#e2e8f0",
  },
  btnRow: {
    display: "flex",
    gap: 12,
  },
  resetBtn: {
    flex: 1,
    padding: "14px 20px",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.1)",
    background: "rgba(255,255,255,0.04)",
    color: "rgba(255,255,255,0.5)",
    fontSize: "0.85rem",
    fontWeight: 500,
    fontFamily: "'DM Sans', sans-serif",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  submitBtn: {
    flex: 2,
    padding: "14px 20px",
    borderRadius: 12,
    border: "none",
    background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
    color: "#fff",
    fontSize: "0.9rem",
    fontWeight: 600,
    fontFamily: "'DM Sans', sans-serif",
    cursor: "pointer",
    boxShadow: "0 4px 20px rgba(34,197,94,0.25)",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  submitBtnDisabled: {
    background: "rgba(255,255,255,0.06)",
    color: "rgba(255,255,255,0.25)",
    boxShadow: "none",
    cursor: "not-allowed",
  },
  spinner: {
    display: "inline-block",
    width: 16,
    height: 16,
    border: "2px solid rgba(255,255,255,0.3)",
    borderTopColor: "#fff",
    borderRadius: "50%",
    animation: "spin 0.6s linear infinite",
  },
  toast: {
    position: "fixed",
    top: 24,
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "12px 20px",
    borderRadius: 12,
    fontSize: "0.85rem",
    fontWeight: 600,
    color: "#fff",
    zIndex: 9999,
    boxShadow: "0 8px 30px rgba(0,0,0,0.4)",
  },
  toastSuccess: {
    background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
    border: "1px solid rgba(34,197,94,0.3)",
  },
  toastError: {
    background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
    border: "1px solid rgba(239,68,68,0.3)",
  },
};

export default ChangePassword;